import { createFileRoute } from "@tanstack/react-router";
import { BookingProvider } from "@/components/site/booking-context";
import { Navbar } from "@/components/site/Navbar";
import { Hero } from "@/components/site/Hero";
import { AvailabilityBar } from "@/components/site/AvailabilityBar";
import { DestinationStory } from "@/components/site/DestinationStory";
import { DeferredHomeBelowFold } from "@/components/site/deferred-home-below-fold";
import { MobileBookingBar } from "@/components/site/MobileBookingBar";
import { heroPreloadLink } from "@/data/optimized-images";
import { faqs, resort } from "@/data/resort";
import { absoluteUrl } from "@/lib/site";
import heroMainAsset from "@/assets/auburn-hero-main.webp.asset.json";

const title = "Auburn RV Resort | Full Hookup RV Sites in Auburn, CA";
const description =
  "Full-hookup RV sites in Auburn, CA. Big-rig friendly with daily, weekly and monthly stays in the Sierra Foothills.";

const schema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": ["LodgingBusiness", "Campground"],
      name: resort.name,
      description,
      url: "/",
      telephone: resort.phone,
      email: resort.email,
      address: {
        "@type": "PostalAddress",
        streetAddress: resort.address.street,
        addressLocality: resort.address.city,
        addressRegion: resort.address.state,
        postalCode: resort.address.zip,
        addressCountry: "US",
      },
      petsAllowed: true,
      amenityFeature: [
        "Full Hookups",
        "20/30/50 Amp Service",
        "Wi-Fi",
        "Laundry",
        "Dog Park",
        "BBQ Areas",
      ].map((name) => ({ "@type": "LocationFeatureSpecification", name, value: true })),
    },
    {
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};

const ogImage = `https://auburnrvresort.com${heroMainAsset.url}`;

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:image", content: ogImage },
      { property: "og:image:width", content: "1322" },
      { property: "og:image:height", content: "936" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: ogImage },
    ],
    links: [{ rel: "canonical", href: absoluteUrl("/") }, heroPreloadLink],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(schema) }],
  }),
  component: Home,
});

function Home() {
  return (
    <BookingProvider>
      <Navbar />
      <main>
        <Hero />
        <AvailabilityBar />
        <DestinationStory />
        <DeferredHomeBelowFold />
      </main>
      <MobileBookingBar />
    </BookingProvider>
  );
}
